import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import {
  purchaseItemService,
  PurchaseItemDTO,
  SupplierPricingRowRequest,
} from '../../services/purchaseItemService';
import { supplierService } from '../../services/supplierService';
import taxService, { Tax } from '../../services/taxService';
import './PurchaseItemPage.css';
import { ArrowLeft, Save, Plus, X } from 'lucide-react';
import { useCurrency } from '../../hooks/useCurrency';
import { parseApiError } from '../../utils/errorUtils';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_LENGTHS = {
  supplierItemCode: 50,
  description: 200,
};

const CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'INR', 'LKR'];

// ─── Types ────────────────────────────────────────────────────────────────────

interface SupplierRow {
  _localId: string;
  /** Populated when loaded from an existing PurchaseItem; undefined for newly added rows. */
  purchaseItemId?: number;
  supplierId: number;
  supplierItemCode: string;
  purchasePrice: number;
  currency: string;
  minimumOrderQuantity: number;
  leadTimeDays: number;
  description: string;
  taxId?: number;
}

interface RowErrors {
  supplierId?: string;
  purchasePrice?: string;
  minimumOrderQuantity?: string;
  leadTimeDays?: string;
  supplierItemCode?: string;
  description?: string;
}

interface FormErrors {
  suppliers?: string;
  rows?: Record<string, RowErrors>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const newRow = (defaultCurrency: string): SupplierRow => ({
  _localId: Math.random().toString(36).substring(2, 11),
  supplierId: 0,
  supplierItemCode: '',
  purchasePrice: 0,
  currency: defaultCurrency,
  minimumOrderQuantity: 0,
  leadTimeDays: 0,
  description: '',
  taxId: undefined,
});

const dtoToRow = (dto: PurchaseItemDTO): SupplierRow => ({
  _localId: Math.random().toString(36).substring(2, 11),
  purchaseItemId: dto.purchaseItemId,
  supplierId: dto.supplierId,
  supplierItemCode: dto.supplierItemCode ?? '',
  purchasePrice: dto.purchasePrice,
  currency: dto.currency,
  minimumOrderQuantity: dto.minimumOrderQuantity,
  leadTimeDays: dto.leadTimeDays,
  description: dto.description ?? '',
  taxId: dto.taxId,
});

// ─── Component ────────────────────────────────────────────────────────────────

const PurchaseItemEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const defaultCurrency = useCurrency();

  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FormErrors>({});

  // Product info (read-only)
  const [productId, setProductId] = useState<number>(0);
  const [productName, setProductName] = useState('');
  const [productSKU, setProductSKU] = useState('');

  const [rows, setRows] = useState<SupplierRow[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [taxes, setTaxes] = useState<Tax[]>([]);

  useEffect(() => {
    if (!id) {
      setError(t('purchaseItem.itemNotFound'));
      setPageLoading(false);
      return;
    }
    loadData();
    taxService.getTaxes(1, 200).then(res => {
      const list: Tax[] = res?.data ?? [];
      setTaxes(list.filter(tx => tx.isActive && (tx.applicableFor === 'Purchase' || tx.applicableFor === 'Both')));
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    try {
      const [itemRes, supRes] = await Promise.all([
        purchaseItemService.getPurchaseItemById(parseInt(id!)),
        supplierService.getSuppliers({ pageSize: 1000 }),
      ]);

      if (supRes.data.data) setSuppliers(supRes.data.data);

      const pivot = itemRes?.data?.data as PurchaseItemDTO | null;
      if (!pivot) {
        setError(t('purchaseItem.itemNotFound'));
        setPageLoading(false);
        return;
      }

      setProductId(pivot.productId);
      setProductName(pivot.productName);
      setProductSKU(pivot.productSKU);

      // Load ALL active supplier rows for this product
      const allRes = await purchaseItemService.getPurchaseItemsByProduct(pivot.productId);
      const allItems: PurchaseItemDTO[] = (allRes?.data?.data as PurchaseItemDTO[]) ?? [];
      setRows(allItems.length > 0 ? allItems.map(dtoToRow) : [newRow(defaultCurrency)]);

      setPageLoading(false);
    } catch (err: unknown) {
      setError(parseApiError(err, t('purchaseItem.failedToLoadItem')).message);
      setPageLoading(false);
    }
  };

  // ── Row field change ────────────────────────────────────────────────────────

  const handleRowChange = useCallback(
    (localId: string, field: keyof SupplierRow, rawValue: string) => {
      setRows(prev =>
        prev.map(r => {
          if (r._localId !== localId) return r;

          let value: string | number = rawValue;

          if (field === 'supplierId') {
            value = parseInt(rawValue) || 0;
          } else if (field === 'taxId') {
            return { ...r, taxId: parseInt(rawValue) || undefined };
          } else if (field === 'purchasePrice') {
            value = parseFloat(rawValue) || 0;
          } else if (field === 'minimumOrderQuantity' || field === 'leadTimeDays') {
            value = parseInt(rawValue) ?? 0;
          } else if (field === 'supplierItemCode' && rawValue.length > MAX_LENGTHS.supplierItemCode) {
            value = rawValue.substring(0, MAX_LENGTHS.supplierItemCode);
          } else if (field === 'description' && rawValue.length > MAX_LENGTHS.description) {
            value = rawValue.substring(0, MAX_LENGTHS.description);
          }

          return { ...r, [field]: value };
        })
      );

      setFieldErrors(prev => ({
        ...prev,
        rows: {
          ...prev.rows,
          [localId]: { ...prev.rows?.[localId], [field]: undefined },
        },
      }));
    },
    []
  );

  const addRow = () => setRows(prev => [...prev, newRow(defaultCurrency || 'USD')]);

  const removeRow = (localId: string) =>
    setRows(prev => prev.filter(r => r._localId !== localId));

  // ── Validation ──────────────────────────────────────────────────────────────

  const validateForm = (): boolean => {
    const errors: FormErrors = { rows: {} };
    let isValid = true;

    if (rows.length === 0) {
      errors.suppliers = t('purchaseItem.formValidation.atLeastOneSupplier');
      isValid = false;
    }

    const supplierCounts = rows.reduce<Record<number, number>>((acc, r) => {
      if (r.supplierId) acc[r.supplierId] = (acc[r.supplierId] ?? 0) + 1;
      return acc;
    }, {});
    if (Object.values(supplierCounts).some(c => c > 1)) {
      errors.suppliers = t('purchaseItem.formValidation.duplicateSupplier');
      isValid = false;
    }

    for (const row of rows) {
      const rowErr: RowErrors = {};

      if (!row.supplierId) {
        rowErr.supplierId = t('purchaseItem.formValidation.supplierRequired');
        isValid = false;
      }
      if (row.purchasePrice <= 0) {
        rowErr.purchasePrice = t('purchaseItem.formValidation.purchasePriceRequired');
        isValid = false;
      }
      if (row.minimumOrderQuantity < 0) {
        rowErr.minimumOrderQuantity = t('purchaseItem.formValidation.moqRequired');
        isValid = false;
      }
      if (row.leadTimeDays < 0) {
        rowErr.leadTimeDays = t('purchaseItem.formValidation.leadTimeInvalid');
        isValid = false;
      }
      if (row.supplierItemCode.length > MAX_LENGTHS.supplierItemCode) {
        rowErr.supplierItemCode = t('purchaseItem.formValidation.supplierItemCodeMaxLength');
        isValid = false;
      }
      if (row.description.length > MAX_LENGTHS.description) {
        rowErr.description = t('purchaseItem.formValidation.descriptionMaxLength');
        isValid = false;
      }

      if (Object.keys(rowErr).length > 0) errors.rows![row._localId] = rowErr;
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError =
        errors.suppliers ||
        Object.values(errors.rows ?? {})
          .flatMap(re => Object.values(re))
          .find(e => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  // ── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!validateForm()) return;

    setSaving(true);
    try {
      const supplierRows: SupplierPricingRowRequest[] = rows.map(r => ({
        purchaseItemId: r.purchaseItemId,
        supplierId: r.supplierId,
        supplierItemCode: r.supplierItemCode,
        purchasePrice: r.purchasePrice,
        currency: r.currency,
        minimumOrderQuantity: r.minimumOrderQuantity,
        leadTimeDays: r.leadTimeDays,
        description: r.description || undefined,
        taxId: r.taxId || undefined,
      }));

      await purchaseItemService.upsertProductSuppliers(productId, { suppliers: supplierRows });
      navigate('/purchase-items', {
        state: { message: t('purchaseItem.itemUpdatedSuccess') },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('purchaseItem.failedToUpdateItem')).message);
      setSaving(false);
    }
  };

  // ── Loading / not-found states ──────────────────────────────────────────────

  if (pageLoading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!productId) {
    return (
      <Layout>
        <div style={{ padding: '24px' }}>
          <div className="alert alert-danger">{error || t('purchaseItem.itemNotFound')}</div>
          <button className="btn btn-secondary" onClick={() => navigate('/purchase-items')}>
            {t('purchaseItem.backToList')}
          </button>
        </div>
      </Layout>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="pi-edit-page">

        {/* ── Top bar ── */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button
              type="button"
              className="ims-back-btn"
              onClick={() => navigate('/purchase-items')}
            >
              <ArrowLeft size={14} strokeWidth={2} />
              <span>{t('purchaseItem.backToList')}</span>
            </button>
            <h1 className="ims-page-title">{t('purchaseItem.editTitle')}</h1>
          </div>
          <div className="ims-top-bar-right">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => navigate('/purchase-items')}
              disabled={saving}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? (
                <>
                  <span className="ims-btn-spinner" />
                  <span className="pi-btn-full">{t('common.saving')}</span>
                </>
              ) : (
                <>
                  <Save size={15} strokeWidth={2} />
                  <span className="pi-btn-full">{t('common.saveChanges')}</span>
                  <span className="pi-btn-short">{t('common.save')}</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div className="ims-error-banner ims-alert ims-alert-error">
            {error}
            <button
              type="button"
              onClick={() => setError(null)}
              style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}
            >
              ✕
            </button>
          </div>
        )}

        {/* ── 3-column body ── */}
        <div className="ims-create-body">

          {/* LEFT — Product Info (read-only) ────────────────────────────────── */}
          <div className="ims-panel-left">
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('purchaseItem.productInformation')}</h2>
              </div>
              <div className="ims-card-body">
                <div className="ims-form-group">
                  <span className="ims-form-label">{t('purchaseItem.productSKU')}</span>
                  <p style={{ margin: 0, fontFamily: 'monospace', fontSize: 13, fontWeight: 600, color: 'var(--ims-text-main)' }}>
                    {productSKU}
                  </p>
                </div>
                <div className="ims-form-group" style={{ marginTop: 12 }}>
                  <span className="ims-form-label">{t('purchaseItem.productName')}</span>
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--ims-text-sub)' }}>
                    {productName}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CENTER — Supplier Pricing Table ────────────────────────────────── */}
          <div className="ims-panel-center">
            <div className="ims-card ims-lines-card">
              <div className="ims-lines-header">
                <h2 className="ims-card-title">{t('purchaseItem.supplierPricing')}</h2>
                {fieldErrors.suppliers && (
                  <span className="pi-suppliers-error">{fieldErrors.suppliers}</span>
                )}
                <button
                  type="button"
                  className="ims-btn ims-btn-secondary"
                  onClick={addRow}
                >
                  <Plus size={13} strokeWidth={2} />
                  {t('purchaseItem.addSupplier')}
                </button>
              </div>

              <div className="ims-lines-body">
                <div className="ims-table-scroll">
                  <table className="ims-table">
                    <thead>
                      <tr>
                        <th className="ims-th">
                          {t('purchaseItem.supplierId')} <span className="required">*</span>
                        </th>
                        <th className="ims-th ims-th--num">
                          {t('purchaseItem.purchasePrice')} <span className="required">*</span>
                        </th>
                        <th className="ims-th">
                          {t('purchaseItem.currency')} <span className="required">*</span>
                        </th>
                        <th className="ims-th ims-th--num">{t('purchaseItem.minimumOrderQuantity')}</th>
                        <th className="ims-th ims-th--num">{t('purchaseItem.leadTimeDays')}</th>
                        <th className="ims-th">{t('purchaseItem.supplierItemCode')}</th>
                        <th className="ims-th">{t('purchaseItem.description')}</th>
                        <th className="ims-th">{t('purchaseItem.tax')}</th>
                        <th className="ims-th ims-th--del" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="ims-td pi-empty-row">
                            {t('purchaseItem.noSuppliersYet')}
                          </td>
                        </tr>
                      ) : (
                        rows.map(row => {
                          const rowErr = fieldErrors.rows?.[row._localId] ?? {};
                          return (
                            <tr key={row._localId} className="ims-tr">

                              {/* Supplier */}
                              <td className="ims-td">
                                <select
                                  className={`ims-cell-input${rowErr.supplierId ? ' is-invalid' : ''}`}
                                  value={row.supplierId || ''}
                                  onChange={e => handleRowChange(row._localId, 'supplierId', e.target.value)}
                                  title={rowErr.supplierId}
                                >
                                  <option value="">{t('purchaseItem.selectSupplier')}</option>
                                  {suppliers.map(s => (
                                    <option key={s.supplierId ?? s.id} value={s.supplierId ?? s.id}>
                                      {s.code} — {s.name}
                                    </option>
                                  ))}
                                </select>
                              </td>

                              {/* Purchase Price */}
                              <td className="ims-td ims-td--num">
                                <input
                                  type="number"
                                  className={`ims-cell-input ims-num-input${rowErr.purchasePrice ? ' is-invalid' : ''}`}
                                  value={row.purchasePrice || ''}
                                  onChange={e => handleRowChange(row._localId, 'purchasePrice', e.target.value)}
                                  placeholder="0.00"
                                  step="0.01"
                                  min="0.01"
                                  title={rowErr.purchasePrice}
                                />
                              </td>

                              {/* Currency */}
                              <td className="ims-td">
                                <select
                                  className="ims-cell-input"
                                  value={row.currency}
                                  onChange={e => handleRowChange(row._localId, 'currency', e.target.value)}
                                >
                                  {CURRENCIES.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                  ))}
                                </select>
                              </td>

                              {/* MOQ */}
                              <td className="ims-td ims-td--num">
                                <input
                                  type="number"
                                  className={`ims-cell-input ims-num-input${rowErr.minimumOrderQuantity ? ' is-invalid' : ''}`}
                                  value={row.minimumOrderQuantity}
                                  onChange={e => handleRowChange(row._localId, 'minimumOrderQuantity', e.target.value)}
                                  min="0"
                                  placeholder="0"
                                  title={rowErr.minimumOrderQuantity}
                                />
                              </td>

                              {/* Lead Time */}
                              <td className="ims-td ims-td--num">
                                <input
                                  type="number"
                                  className={`ims-cell-input ims-num-input${rowErr.leadTimeDays ? ' is-invalid' : ''}`}
                                  value={row.leadTimeDays}
                                  onChange={e => handleRowChange(row._localId, 'leadTimeDays', e.target.value)}
                                  min="0"
                                  placeholder="0"
                                  title={rowErr.leadTimeDays}
                                />
                              </td>

                              {/* Supplier Item Code */}
                              <td className="ims-td">
                                <input
                                  type="text"
                                  className={`ims-cell-input${rowErr.supplierItemCode ? ' is-invalid' : ''}`}
                                  value={row.supplierItemCode}
                                  onChange={e => handleRowChange(row._localId, 'supplierItemCode', e.target.value)}
                                  maxLength={MAX_LENGTHS.supplierItemCode}
                                  placeholder="e.g. SUP-001"
                                  title={rowErr.supplierItemCode}
                                />
                              </td>

                              {/* Description */}
                              <td className="ims-td">
                                <input
                                  type="text"
                                  className={`ims-cell-input${rowErr.description ? ' is-invalid' : ''}`}
                                  value={row.description}
                                  onChange={e => handleRowChange(row._localId, 'description', e.target.value)}
                                  maxLength={MAX_LENGTHS.description}
                                  placeholder={t('purchaseItem.description')}
                                  title={rowErr.description}
                                />
                              </td>

                              {/* Tax */}
                              <td className="ims-td">
                                <select
                                  className="ims-cell-input"
                                  value={row.taxId ?? ''}
                                  onChange={e => handleRowChange(row._localId, 'taxId', e.target.value)}
                                  title={t('purchaseItem.taxHint')}
                                >
                                  <option value="">{t('purchaseItem.noTax')}</option>
                                  {taxes.map(tax => (
                                    <option key={tax.taxId} value={tax.taxId}>
                                      {tax.taxName} ({tax.taxPercentage}%)
                                    </option>
                                  ))}
                                </select>
                              </td>

                              {/* Delete */}
                              <td className="ims-td ims-td--del">
                                <button
                                  type="button"
                                  className="ims-remove-btn"
                                  onClick={() => removeRow(row._localId)}
                                  title={t('purchaseItem.removeSupplier')}
                                >
                                  <X size={14} strokeWidth={2} />
                                </button>
                              </td>

                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Summary + Help ─────────────────────────────────────────── */}
          <div className="ims-panel-right">
            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('purchaseItem.supplierPricing')}</h3>
              </div>
              <div className="ims-card-body">
                <div className="ims-summary-row">
                  <span className="ims-summary-label">{t('purchaseItem.supplierId')}s</span>
                  <span className="ims-summary-value">{rows.length}</span>
                </div>
              </div>
            </div>

            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('purchaseItem.requiredFields')}</h3>
              </div>
              <div className="ims-card-body">
                <ul className="ims-help-list">
                  <li>{t('purchaseItem.supplierId')}</li>
                  <li>{t('purchaseItem.purchasePrice')}</li>
                  <li>{t('purchaseItem.currency')}</li>
                </ul>
                <hr style={{ margin: '8px 0', borderColor: '#f3f4f6' }} />
                <p style={{ fontSize: 11, fontWeight: 600, color: '#374151', margin: '8px 0 4px', textTransform: 'uppercase', letterSpacing: '.4px' }}>
                  {t('purchaseItem.optionalFields')}
                </p>
                <ul className="ims-help-list">
                  <li>{t('purchaseItem.minimumOrderQuantity')} (≥ 0)</li>
                  <li>{t('purchaseItem.leadTimeDays')} (≥ 0)</li>
                  <li>{t('purchaseItem.supplierItemCode')}</li>
                  <li>{t('purchaseItem.description')}</li>
                  <li>{t('purchaseItem.tax')}</li>
                </ul>
              </div>
            </div>
          </div>

        </div>
      </form>
    </Layout>
  );
};

export default PurchaseItemEditPage;
